import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth } from '../firebase';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getUserProfile, getReportTypes } from '../utils/firebaseUtils';
import type { UserProfile } from '../utils/firebaseUtils';
import type { ReportType } from '../types/ReportType';
import { getUserCompanies } from '../utils/userUtils';
import { getCompanySubscriptions } from '../utils/subscriptionUtils';
import type { CompanySubscriptions } from '../utils/subscriptionUtils';
import type { UserCompanyRole } from '../utils/userCompaniesInterface';
import { getUserCompaniesForSuperAdmin } from '../utils/companyUtils';
import { subscribeToPushNotifications } from '../utils/pushNotifications';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  reportTypes: ReportType[];
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  userCompanies: UserCompanyRole[];
  subscriptions: CompanySubscriptions[];
  hasFeature: (feature: string, companyId?: string) => boolean;
  selectedCompany: UserCompanyRole | null;
  selectedCompanyProfile: UserCompanyRole | null;
  setSelectedCompanyId: (companyId: string | null) => void;
  refreshUserProfile: () => Promise<void>;
  // template helpers
  loadCompanyTemplate: (companyId: string) => Promise<any | null>;
  getTemplateLabel: (companyId: string | undefined | null, key: string, defaultLabel?: string) => string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
  const [loading, setLoading] = useState(true);
  const [userCompanies, setUserCompanies] = useState<UserCompanyRole[]>([]);
  const [subscriptions, setSubscriptions] = useState<CompanySubscriptions[]>([]);
  const [selectedCompanyId, setSelectedCompanyIdState] = useState<string | null>(null);
  const [selectedCompanyProfile, setSelectedCompanyProfile] = useState<UserCompanyRole | null>(null);
  const [templates, setTemplates] = useState<Record<string, any>>({});

  // Custom setter to update both selectedCompanyId and selectedCompanyProfile
  const setSelectedCompanyId = (companyId: string | null) => {
    setSelectedCompanyIdState(companyId);
    const found = companyId ? userCompanies.find(c => c.companyId === companyId) || null : null;
    setSelectedCompanyProfile(found);
  };

  const hasFeature = (feature: string, companyId?: string): boolean => {
    try {
      const effectiveCompanyId = companyId || selectedCompanyId;
      if (!effectiveCompanyId) return false;
      
      // 1. Check if the company has an active subscription
      const subscription = subscriptions.find(sub => sub.companyId === effectiveCompanyId && sub.isActive === true);
      if (!subscription) return false;

      // 2. Check if the user has the feature in their permissions for this company (prefix match)
      const userCompany = userCompanies.find(uc => uc.companyId === effectiveCompanyId && uc.active === true && uc.role === 'user');
      if (userCompany && userCompany.permissions && userCompany.permissions.some(p => p.startsWith(feature))) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking feature availability:', error);
      return false;
    }
  }

  useEffect(() => {
    const found = userCompanies.find(c => c.companyId === selectedCompanyId) || null;
    setSelectedCompanyProfile(found);
  }, [selectedCompanyId, userCompanies]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      console.log('auth context ', user)
      if (user) {
        try {
          const profile = await getUserProfile(user.uid);
          setUserProfile(profile || null);
          console.log('auth context->--->>>',{profile})
          // Only load report types after authentication
          try {
            const types = await getReportTypes();
            setReportTypes(types);
            // Set user companies if available
            if (profile) {
              let _userCompanies: any;
              if (profile.globalRole === 'superadmin') {
                _userCompanies = await getUserCompaniesForSuperAdmin(profile);
              } else {
                _userCompanies = await getUserCompanies(profile.uid);
                
              }
              console.log('🏢 AuthContext - User Companies Loaded:', _userCompanies.map((uc: any) => ({
                companyId: uc.companyId,
                companyName: uc.companyName,
                hasConfig: !!uc.companyConfig,
                isHideGPR_UserApp: uc.companyConfig?.isHideGPR_UserApp,
                fullConfig: uc.companyConfig
              })));
              setUserCompanies(_userCompanies);
              // Set default selected company
              if (_userCompanies.length > 0) {
                setSelectedCompanyId(_userCompanies[0].companyId);
              } else {
                setSelectedCompanyId(null);
              }
              // Load subscriptions for user companies
              const companyIds = _userCompanies.map((uc: any) => uc.companyId);
              const subscriptionsResult = await getCompanySubscriptions(companyIds);
              setSubscriptions(subscriptionsResult);

              // Subscribe to push notifications after user setup is complete
              if (user.email && _userCompanies.length > 0) {
                try {
                  await subscribeToPushNotifications(
                    user.uid,
                    user.email,
                    _userCompanies[0].companyId
                  );
                  console.log('Push notification subscription successful');
                } catch (error) {
                  console.error('Failed to subscribe to push notifications:', error);
                }
              }
            }
          } catch (error) {
            console.error('Error:', error);
            setReportTypes([]);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
        setReportTypes([]);
        setSelectedCompanyId(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // get the admin roles - check all companies the user belongs to (only active companies)
  const isAdmin = userCompanies.some(c => c.role === 'admin' && c.active === true);
  const isSuperAdmin = userProfile?.globalRole === 'superadmin' && userProfile?.active === true;

  const selectedCompany = userCompanies.find(c => c.companyId === selectedCompanyId) || null;
  // Keep selectedCompanyProfile in sync if userCompanies or selectedCompanyId changes
  useEffect(() => {
    if (selectedCompanyId) {
      const found = userCompanies.find(c => c.companyId === selectedCompanyId) || null;
      setSelectedCompanyProfile(found);
    } else {
      setSelectedCompanyProfile(null);
    }
  }, [selectedCompanyId, userCompanies]);

  // Fetch and update the latest user profile from Firestore
  const refreshUserProfile = async () => {
    if (user && user.uid) {
      try {
        const latestProfile = await getUserProfile(user.uid);
        setUserProfile(latestProfile || null);
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    }
  };

  // Template loader: fetches companies/<companyId>/templates/delivery document and caches it
  const loadCompanyTemplate = async (companyId: string) => {
    if (!companyId) return null;
    try {
      if (templates[companyId]) return templates[companyId];
      const tplDoc = await getDoc(doc(db, 'companies', companyId, 'templates', 'delivery'));
      if (tplDoc.exists()) {
        const data = tplDoc.data();
        setTemplates(prev => ({ ...prev, [companyId]: data }));
        return data;
      }
      setTemplates(prev => ({ ...prev, [companyId]: null }));
      return null;
    } catch (error) {
      console.error('Error loading company template:', error);
      return null;
    }
  };

  // Ensure template is preloaded whenever selectedCompanyId changes
  useEffect(() => {
    if (selectedCompanyId) {
      // fire-and-forget; cache will cause re-render when ready
      loadCompanyTemplate(selectedCompanyId).catch(() => {});
    }
  }, [selectedCompanyId]);

  const getTemplateLabel = (companyId: string | undefined | null, key: string, defaultLabel?: string) => {
    if (!companyId) return defaultLabel || key;
    const tpl = templates[companyId];
    try {
      if (tpl && typeof tpl === 'object') {
        // templates may store labels under `form`, `labels`, or at the root — try in that order
        if (tpl.form && tpl.form[key]) return tpl.form[key];
        if (tpl.labels && tpl.labels[key]) return tpl.labels[key];
        if (tpl[key]) return tpl[key];
      }
    } catch (e) {
      // ignore
    }
    return defaultLabel || key;
  };

  return (
    <AuthContext.Provider value={{
      user,
      userProfile,
      reportTypes,
      loading,
      isAdmin,
      isSuperAdmin,
      userCompanies,
      subscriptions,
      hasFeature,
      selectedCompany,
      selectedCompanyProfile,
      setSelectedCompanyId,
      refreshUserProfile,
      // templates
      loadCompanyTemplate,
      getTemplateLabel,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
